import React, { useEffect, useState } from 'react'
import i18n from '@pureartisan/simple-i18n'
import { makeStyles } from '@material-ui/core/styles'
import Tooltip from '../Tooltip'
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery,
} from '@material-ui/core'
import shallow from 'zustand/shallow'

import useStore from '../store'
import LinearScale from '../LinearScale'
import ScaleTrianglesIcon from '../../../assets/Icons/ScaleTrianglesIcon'
import ScalePointIcon from '../../../assets/Icons/ScalePointIcon'
import {
  getCountyFromGeoid,
  getIdentifierPhrase,
  getLevelArray,
  getStateNameFromFips,
} from '../utils'
import IndexWithRating from '../IndexWithRating/IndexWithRating'
import _ from 'lodash'
import { theme, variables } from '../theme'
import StackedHorizontalBar from './StackedHorizontalBar'
import { DEMOS_BY_COI } from '../../../../constants/map'
import ChartContainer from '../Chart/ChartContainer'
import BarChartSmIcon from '../../../assets/Icons/BarChartSmIcon'
import { ArrowForwardIos } from '@material-ui/icons'
import clsx from 'clsx'

const useStyles = makeStyles(theme => ({
  root: {
    paddingLeft: theme.spacing(0.5),
  },
  variabilityChart: {
    position: 'absolute',
    left:
      theme.extras.slideoutPanel.width +
      theme.extras.controlPanel.width +
      8,
    [theme.breakpoints.down(
      theme.extras.slideoutPanel.width +
        theme.extras.controlPanel.width +
        theme.extras.chart.width +
        8 * 2,
    )]: {
      left: 'unset',
      right: 8,
    },
    top: '80px',
  },
  legend: {
    display: 'flex',
    justifyContent: 'space-evenly',
    background: theme.extras.variables.colors.alabaster,
    padding: theme.spacing(1),
    margin: theme.spacing(2, 0, 2, 2),
    '& svg': {
      padding: theme.spacing(0, 1),
      marginTop: '-2px',
      verticalAlign: 'middle',
    },
  },
  subdomain: {
    '& .index-with-rating': {
      padding: theme.spacing(2, 0, 1, 2),
      width: `calc(100% - ${theme.spacing(2)}px)`,
      '& .MuiTypography-root': {
        fontWeight: 400,
      },
    },
  },
  indicator: {},
  indicatorNameRow: {
    padding: theme.spacing(2),
  },
  indicatorName: {
    '& svg': {
      margin: 0,
    },
    '& span': {
      paddingRight: theme.spacing(0.5),
    },
  },
  indicatorValue: {
    fontWeight: 500,
    color: theme.extras.variables.colors.textMedium,
  },
  variabilitySection: {
    padding: theme.spacing(2),
    background: theme.extras.variables.colors.accent,
    '& h3': {
      marginBottom: theme.spacing(2),
    },
    '& p.MuiTypography-body1': {
      marginTop: theme.spacing(1.5),
    },
    '& button': {
      width: '100%',
      margin: theme.spacing(2.5, 0, 2.5),
      border: `1px solid ${theme.extras.variables.colors.ddkMainBlue}`,
      '& p': {
        display: 'flex',
        alignItems: 'center',
        color: theme.extras.variables.colors.ddkMainBlue,
      },
      '& .bar-chart-sm-icon': {
        paddingRight: theme.spacing(0.75),
      },
      '& .MuiSvgIcon-root': {
        height: 10,
        position: 'relative',
        right: 0,
        transition: theme.transitions.create(
          ['right', 'transform'],
          {
            duration: 300,
          },
        ),
      },
      '&:hover .MuiSvgIcon-root': {
        right: -6,
      },
      '&.metricActive .MuiSvgIcon-root': {
        transform: 'rotate(180deg)',
        // right: -6,
        right: 106,
      },
      '&:hover.metricActive .MuiSvgIcon-root': {
        // right: 0,
        right: 112,
      },
    },
  },
}))

const VariabilityChartSection = ({
  data,
  domain,
  countyName,
  activeNorm,
  countyId,
}) => {
  const {
    slideoutPanel,
    variabilityChartMetric,
    setStoreValues,
  } = useStore(
    state => ({
      slideoutPanel: state.slideoutPanel,
      variabilityChartMetric: state.variabilityChartMetric,
      setStoreValues: state.setStoreValues,
    }),
    shallow,
  )

  const barOptions = ['t', ...DEMOS_BY_COI]
  const [activeBars, setActiveBars] = useState(barOptions)
  const handleActiveBars = demo => {
    const bars = activeBars.slice()
    const ind = bars.indexOf(demo)
    if (ind >= 0) {
      bars.splice(ind, 1)
    } else {
      bars.push(demo)
    }
    setActiveBars(bars)
  }

  const closeVariabilityChart = () =>
    setStoreValues({ variabilityChartMetric: null })
  useEffect(() => {
    if (
      !slideoutPanel.active ||
      slideoutPanel.panel === 'location'
    ) {
      closeVariabilityChart()
      setActiveBars(barOptions)
    }
  }, [slideoutPanel.active, slideoutPanel.panel])

  const classes = useStyles()

  const metricActive = variabilityChartMetric === domain.key
  const buttonText = metricActive
    ? 'Hide chart'
    : 'View chart'

  const stateAbbr = getStateNameFromFips(
    countyId,
    'abbr',
  )

  const theme = useTheme()
  // no chart on mobile
  if (useMediaQuery(theme.breakpoints.down('xs')))
    return null
  return (
    <>
      <Button
        onClick={() =>
          setStoreValues({
            variabilityChartMetric: metricActive
              ? null
              : domain.key,
          })
        }
        className={clsx({ metricActive })}
      >
        <Typography variant="body2">
          <BarChartSmIcon className={classes.controlIcon} />
          {buttonText}
          <ArrowForwardIos />
        </Typography>
      </Button>
      {metricActive && (
        <ChartContainer
          className={classes.variabilityChart}
          data={data}
          handleActivePointLayers={handleActiveBars}
          handleClose={closeVariabilityChart}
          barOptions={barOptions}
          activeBars={activeBars}
          title={i18n.translate('VARIABILITY_CHART_TITLE', {
            domain: i18n.translate(
              `LABEL_LEVEL_${domain.key}`,
            ),
          })}
          subtitle={i18n.translate(
            'VARIABILITY_CHART_SUBTITLE',
            {
              countyName,
              stateAbbr,
              norm: i18n.translate(
                `LEGEND_NORMLY_${activeNorm}`,
              ),
            },
          )}
        />
      )}
    </>
  )
}

const getSubdomainRating = ({
  data,
  subdomainId,
  norm,
  isScore,
}) => {
  // TODO standardize key formatting
  const key = `${
    isScore ? 's_' : ''
  }${subdomainId.toLowerCase().replace('_', '')}_${norm}`
  return data[key]
}

// Displays a list of indicator scales with legend on top
const DomainDetailsSection = ({ domain }) => {
  const classes = useStyles()

  const {
    remoteJson,
    slideoutFeatureId,
    slideoutFeature,
    activeGeoType,
    activeNorm,
    activeYear,
    loadYears,
    activeMetric,
  } = useStore(
    state => ({
      remoteJson: state.remoteJson,
      slideoutFeatureId: state.slideoutFeatureId,
      slideoutFeature: state.slideoutFeature,
      activeGeoType: state.activeGeoType,
      activeNorm: state.activeNorm,
      activeYear: state.activeYear,
      loadYears: state.loadYears,
      activeMetric: state.activeMetric,
    }),
    shallow,
  )

  const rawGeoData = remoteJson.raw.data.find(el => {
    return Number(el.GEOID) === slideoutFeatureId
  })

  // console.log(domain.subdomains)
  if (!rawGeoData) {
    return (
      <CircularProgress
        aria-label="loading progress"
        color="primary"
        style={{ marginLeft: 100 }}
      />
    )
  }

  const getLegend = () => {
    return (
      <Box className={classes.legend}>
        <Typography variant="body2" color="textPrimary">
          <ScaleTrianglesIcon />
          {getIdentifierPhrase(
            activeGeoType,
            slideoutFeatureId,
            {
              truncate: true,
            },
          )}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          <ScalePointIcon />
          {i18n.translate(
            // `LEGEND_NORM_ADJ_${activeNorm.toUpperCase()}`,
            `LEGEND_NORM_ADJ_N`, // for now always compared to national mean
          )}{' '}
          {i18n.translate(`SCALE_MEAN`, {
            year: loadYears.slice(-1)[0],
          })}
        </Typography>
      </Box>
    )
  }

  // TODO: remove this once client updates description in data
  const HACKFixClientTooltip = str =>
    str.replace(
      'recreational sports leagues, and mentoring programs',
      'recreational sports leagues and mentoring programs',
    )
  // TODO: remove this once client updates title in data
  const HACKFixClientIndicatorName = str =>
    str.replace(
      'professional of doctoral degrees',
      'professional or doctoral degrees',
    )
  const getIndicatorTitle = ({
    indicator_name,
    indicator_description,
  }) => (
    <Box
      display="flex"
      justifyContent="space-between"
      className={classes.indicatorNameRow}
    >
      <Typography
        variant="body2"
        className={classes.indicatorName}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: HACKFixClientIndicatorName(
              indicator_name,
            ),
          }}
        />
        {!!indicator_description && (
          <Tooltip
            title={HACKFixClientTooltip(
              indicator_description,
            )}
          />
        )}
      </Typography>
    </Box>
  )

  const getVariabilitySection = () => {
    const countyId = String(slideoutFeatureId).padStart(
      5,
      '0',
    )
    const variabilityData = _.get(remoteJson, [
      'barcharts',
      'data',
      `20${activeYear}`,
      'counties',
      countyId,
      activeNorm,
      domain.key,
    ])
    if (!variabilityData) {
      console.log(
        'xxxxx no variability data for',
        slideoutFeatureId,
        activeYear,
        activeNorm,
        activeMetric,
        variabilityData,
        remoteJson?.barcharts,
      )
      return null
    } else if (variabilityData.length !== 5) {
      console.log(
        'xxxxx incomplete var data for ',
        slideoutFeatureId,
        activeYear,
        activeNorm,
        activeMetric,
        variabilityData,
        remoteJson?.barcharts,
      )
      return null
    }

    const countyLevel =
      slideoutFeature?.properties[
        'x' + domain.key + activeNorm
      ]

    const countyName = getCountyFromGeoid(countyId)
    // 't' is the total percent at that level (as opposed a specific r/e)
    const percentAtCountyLevel = _.get(
      variabilityData,
      [countyLevel, 't'],
      0,
    )
    return (
      <Box className={classes.variabilitySection}>
        <Typography variant="h3">
          {i18n.translate('SLIDEOUT_VARIABILITY_TITLE')}
        </Typography>
        <Typography
          variant="body1"
          dangerouslySetInnerHTML={{
            __html: i18n.translate(
              `SLIDEOUT_GEO_VARIABILITY_DESC`,
              {
                percent: 100 - percentAtCountyLevel,
                name: countyName,
                domain: i18n.translate(
                  `LABEL_LEVEL_${domain.key}`,
                ),
              },
            ),
          }}
        />
        {/* <StackedHorizontalBar
          values={countyOverallDist}
          highlightedIdx={countyLevel}
          colors={theme.extras.ratings.levelColors}
          highlightColor={variables.colors.textSubtle}
          barHeight={12}
          legendText={i18n.translate(
            'SLIDEOUT_HSBARCHART_LEGEND',
          )}
          getTooltipText={({ barIdx, value }) =>
            i18n.translate('SLIDEOUT_HSBARCHART_TOOLTIP', {
              percent: value,
              level: getLevelArray()[barIdx],
              domain: domain.domain_name,
            })
          }
        />
        <Typography variant="h3">
          {i18n.translate(
            'SLIDEOUT_RACE_VARIABILITY_TITLE',
          )}
        </Typography> */}
        <Typography variant="body1">
          {i18n.translate(
            'SLIDEOUT_RACE_VARIABILITY_DESC',
            {
              name: countyName,
            },
          )}
        </Typography>
        <VariabilityChartSection
          data={variabilityData}
          domain={domain}
          countyId={countyId}
          countyName={countyName}
          activeNorm={activeNorm}
        />
      </Box>
    )
  }

  const getIndicatorList = subdomain =>
    subdomain.indicators.map(indicator => {
      const value =
        rawGeoData[indicator.indicator.toLowerCase()]
      if (_.isNaN(parseInt(value))) return null
      return (
        <Box
          className={classes.indicator}
          key={`indicator-${indicator.indicator}`}
        >
          {getIndicatorTitle(indicator)}
          <LinearScale
            value={Number(value)}
            meta={indicator.meta}
          />
        </Box>
      )
    })

  const isTract = activeGeoType === 'tracts'
  const isCounty = activeGeoType === 'counties'

  return (
    <Box className={classes.root}>
      {/* if subdomains don't have indicators (ie zips, counties) no legend */}
      {!!isTract && getLegend()}
      {!!isCounty && getVariabilitySection()}
      {domain.subdomains?.map(subdomain => {
        const score = getSubdomainRating({
          data: rawGeoData,
          subdomainId: subdomain.indicator,
          norm: activeNorm,
          isScore: true,
        })
        const level = getSubdomainRating({
          data: rawGeoData,
          subdomainId: subdomain.indicator,
          norm: activeNorm,
        })
        if (_.isNaN(parseInt(level))) return null
        return (
          <Box
            key={subdomain.indicator}
            className={classes.subdomain}
          >
            <IndexWithRating
              index={subdomain.indicator}
              // TODO (ETL): correct instead in source csv in ETL
              name={subdomain.subdomain_name.replace(
                'subdomain',
                '',
              )}
              score={score}
              level={level}
            />
            {!!isTract && getIndicatorList(subdomain)}
          </Box>
        )
      })}
    </Box>
  )
}

export default DomainDetailsSection
